import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box, Typography } from '@mui/material';

import theme from '../../../theme';
import { PRIMARY_COLOR } from '../../../theme/colorConstants';
import Button, { BUTTON_TYPE } from '../../Button';

import ProgressBar from './ProgressBar';

const titleStyling = {
  color: theme?.components?.batchActionModal?.palette?.title?.color,
  fontFamily: theme?.components?.batchActionModal?.typography?.title?.fontFamily,
  fontSize: theme?.components?.batchActionModal?.typography?.title?.fontSize,
  mt: 1,
};

const textStyling = {
  fontFamily: theme?.components?.batchActionModal?.typography?.text?.fontFamily,
  fontSize: theme?.components?.batchActionModal?.typography?.text?.fontSize,
  lineHeight: theme?.components?.batchActionModal?.typography?.text?.lineHeight,
  mt: 1,
};

const ModalBody = ({
  smallScreen,
  headerKey,
  BodyElement,
  actionError,
  hasStarted,
  isComplete,
  isProcessing,
  failedCount,
  updatedCount,
  totalItems,
  cancelButtonKey,
  onClose,
  onGoBack,
  primaryButtonKey,
  handleConfirm,
}) => (
  <Box
    sx={{
      position: 'relative',
      width: smallScreen ? '80vw' : '50vw',
      bgcolor: 'background.paper',
      borderRadius: 3,
      boxShadow: 24,
      p: 1,
      maxHeight: '90vh',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        maxHeight: 'calc(90vh - 16px)',
        overflowY: 'auto',
        padding: '50px',
      }}
    >
      {headerKey && <Typography sx={titleStyling}>{LanguageConverter(headerKey)}</Typography>}

      {BodyElement && <BodyElement />}

      {actionError && (
        <Typography color="error" sx={textStyling}>
          {LanguageConverter(actionError)}
        </Typography>
      )}

      {(hasStarted || isComplete) && (
        <ProgressBar
          isComplete={isComplete}
          isProcessing={isProcessing}
          failedCount={failedCount}
          updatedCount={updatedCount}
          totalCount={totalItems}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          margin: theme.spacing(5),
          gap: 2,
        }}
      >
        {/* FIXME: This should get split apart for clarity. Note that we have two cases here. One
        where we are going back because we are cancelling out. The other where we have finished
        computation.  There is a difference in what these buttons do. We need to reload data when closing
        after having made actual changes to the data. */}
        {(!isProcessing || isComplete) && (
          <Button
            text={LanguageConverter(cancelButtonKey ?? 'button.close')}
            onClick={(isComplete && onClose) || onGoBack}
            sx={{
              color: theme?.components?.batchActionModal?.palette?.cancelButton?.color,
              padding: theme.spacing(2),
              backgroundColor:
                theme?.components?.batchActionModal?.palette?.cancelButton?.backgroundColor,
              border: '1px solid black',
            }}
            hoverColor={PRIMARY_COLOR[20]}
          />
        )}
        {!hasStarted && !actionError && !isComplete && primaryButtonKey && (
          <Button
            data-testid="modalSubmitButton"
            type={BUTTON_TYPE.SUBMIT}
            text={LanguageConverter(primaryButtonKey)}
            disabled={isProcessing}
            onClick={handleConfirm}
            sx={{
              backgroundColor: theme?.components?.batchActionModal?.palette?.primaryButton?.color,
              padding: theme.spacing(2),
            }}
          />
        )}
      </Box>
    </Box>
  </Box>
);

ModalBody.propTypes = {
  smallScreen: PropTypes.bool,
  headerKey: PropTypes.string.isRequired,
  BodyElement: PropTypes.elementType,
  actionError: PropTypes.string,
  hasStarted: PropTypes.bool,
  isComplete: PropTypes.bool,
  isProcessing: PropTypes.bool,
  failedCount: PropTypes.number,
  updatedCount: PropTypes.number,
  totalItems: PropTypes.number,
  cancelButtonKey: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  primaryButtonKey: PropTypes.string.isRequired,
  handleConfirm: PropTypes.func.isRequired,
};

export default ModalBody;
